/**
 * ThemeToggle Component
 * Light/Dark mode toggle — gold accent on hover
 */

import React from 'react';
import { IconButton, Tooltip, useTheme, alpha, Box, Typography } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme as useThemeContext } from '../../contexts/ThemeContext';

export const ThemeToggle = ({ placement = 'default', tooltipPlacement = 'bottom', fullWidth, sx = {}, ...props }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeContext();

  const isLight = mode === 'light';
  const currentLabel = isLight
    ? t('theme_light_mode', { defaultValue: 'Light mode' })
    : t('theme_dark_mode', { defaultValue: 'Dark mode' });
  const tooltipText = isLight
    ? t('theme_switch_to_dark', { defaultValue: 'Switch to dark mode' })
    : t('theme_switch_to_light', { defaultValue: 'Switch to light mode' });
  const Icon = isLight ? Brightness4 : Brightness7;
  const goldColor = theme.palette.primary.main;

  if (fullWidth) {
    return (
      <Tooltip title={tooltipText} placement={tooltipPlacement}>
        <Box
          onClick={toggleTheme}
          role="button"
          aria-label="toggle theme"
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            cursor: 'pointer',
            px: 1.5,
            py: 1,
            borderRadius: '10px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: alpha(goldColor, 0.06),
              borderColor: alpha(goldColor, 0.3),
              color: goldColor,
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            ...sx,
          }}
          {...props}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: '18px' }} />
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '0.005em',
              whiteSpace: 'nowrap',
              color: 'inherit',
            }}
          >
            {currentLabel}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText} placement={tooltipPlacement}>
      <IconButton
        onClick={toggleTheme}
        aria-label="toggle theme"
        size="medium"
        sx={{
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: alpha(goldColor, 0.06),
            borderColor: alpha(goldColor, 0.3),
            color: goldColor,
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
          ...sx,
        }}
        {...props}
      >
        <Icon sx={{ fontSize: '20px' }} />
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;
